import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Clinician } from "types/Clinician";
import { Patient } from "types/Patient";
import { FilterParams, SortConfig, SortDirection } from "types/common";

export interface CurrentClinicianState {
  currentClinician?: Clinician;
  selectedClinician?: Clinician;
  selectedClinicianRoles?: string[];
  cliniciansList?: Clinician[];
  clinicianPatientsList?: Patient[];
  clinicTeamMembers?: Clinician[];
  clinicTeamLeads?: Clinician[];
  clinicRecoveryTemplates?: string[];
  selectedRecoveryTemplate?: string[];
  clinicTeamMembersString?: string[];
  clinicTeamLeadsString?: string[];
  clinicStats?: any[];
  sortConfig: SortConfig;
  loading: boolean;
  clinicianPatientsLoading: boolean;
  updateAvatarLoading: boolean;
  offset: number;
  limit: number;
  total: number;
  jointFilterParams: FilterParams[];
  offsetPatients: number;
  sortConfigPatients: SortConfig;
  limitPatients: number;
  totalPatients: number;
  jointFilterParamsPatients: FilterParams[];
  search: string;
}

const initialState: CurrentClinicianState = {
  currentClinician: undefined,
  selectedClinician: undefined,
  cliniciansList: [],
  clinicTeamMembers: [],
  clinicTeamLeads: [],
  clinicTeamMembersString: [],
  clinicTeamLeadsString: [],
  clinicianPatientsList: [],
  clinicRecoveryTemplates: [],
  selectedRecoveryTemplate: [],
  loading: false,
  clinicianPatientsLoading: false,
  updateAvatarLoading: false,
  offset: 0,
  limit: 0,
  total: 0,
  sortConfig: { sortBy: "name", sortDirection: SortDirection.desc },
  jointFilterParams: [],
  selectedClinicianRoles: [],
  limitPatients: 0,
  totalPatients: 0,
  offsetPatients: 0,
  sortConfigPatients: { sortBy: "name", sortDirection: SortDirection.desc },
  jointFilterParamsPatients: [],
  search: "",
};

export const currentClinicianSlice = createSlice({
  name: "CurrentClinician",
  initialState,
  reducers: {
    setCurrentClinician: (state, { payload }: PayloadAction<Clinician>) => {
      state.currentClinician = payload;
    },
    setSelectedClinician: (
      state,
      { payload }: PayloadAction<Clinician | undefined>
    ) => {
      state.selectedClinician = payload;
    },
    setSelectedClinicianRoles: (
      state,
      { payload }: PayloadAction<string[]>
    ) => {
      state.selectedClinicianRoles = payload;
    },
    setCliniciansList: (state, { payload }: PayloadAction<Clinician[]>) => {
      state.cliniciansList = payload;
    },
    setClinicStats: (state, { payload }: PayloadAction<any>) => {
      state.clinicStats = payload;
    },
    setClinicTeamMembersList: (
      state,
      { payload }: PayloadAction<Clinician[]>
    ) => {
      state.clinicTeamMembers = payload;
    },
    setClinicTeamLeadsList: (
      state,
      { payload }: PayloadAction<Clinician[]>
    ) => {
      state.clinicTeamLeads = payload;
    },
    setClinicTeamMembersStringList: (
      state,
      { payload }: PayloadAction<string[]>
    ) => {
      state.clinicTeamMembersString = payload;
    },
    setClinicTeamLeadsStringList: (
      state,
      { payload }: PayloadAction<string[]>
    ) => {
      state.clinicTeamLeadsString = payload;
    },
    setClinicianPatientsList: (
      state,
      { payload }: PayloadAction<Patient[]>
    ) => {
      state.clinicianPatientsList = payload;
    },
    setClinicianRecoveryTemplates: (
      state,
      { payload }: PayloadAction<string[]>
    ) => {
      state.clinicRecoveryTemplates = payload;
    },
    removeRecoveryTemplate: (state, { payload }: PayloadAction<any>) => {
      // @ts-ignore
      state.clinicRecoveryTemplates = state.clinicRecoveryTemplates?.filter((element) => element?._id !== payload
      );
    },
    setSelectedRecoveryTemplate: (state, { payload }: PayloadAction<any>) => {
      state.selectedRecoveryTemplate = payload;
    },
    removeCurrentClinician: (state) => {
      state.currentClinician = undefined;
    },
    setLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setClinicianPatientsLoading: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.clinicianPatientsLoading = payload;
    },
    setUpdateAvatarLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.updateAvatarLoading = payload;
    },
    setOffset: (state, { payload }: PayloadAction<number>) => {
      state.offset = payload;
    },
    setLimit: (state, { payload }: PayloadAction<number>) => {
      state.limit = payload;
    },
    setTotal: (state, { payload }: PayloadAction<number>) => {
      state.total = payload;
    },
    setSortConfig: (state, { payload }: PayloadAction<SortConfig>) => {
      state.sortConfig = payload;
    },
    setJointFilterParams: (
      state,
      { payload }: PayloadAction<FilterParams[]>
    ) => {
      state.jointFilterParams = payload;
    },
    setLimitPatients: (state, { payload }: PayloadAction<number>) => {
      state.limitPatients = payload;
    },
    setTotalPatients: (state, { payload }: PayloadAction<number>) => {
      state.totalPatients = payload;
    },
    setSearch: (state, { payload }: PayloadAction<string>) => {
      state.search = payload;
    },
    setOffsetPatients: (state, { payload }: PayloadAction<number>) => {
      state.offsetPatients = payload;
    },
    setSortConfigPatients: (state, { payload }: PayloadAction<SortConfig>) => {
      state.sortConfigPatients = payload;
    },
    setJointFilterParamsPatients: (
      state,
      { payload }: PayloadAction<FilterParams[]>
    ) => {
      state.jointFilterParamsPatients = payload;
    },
  },
});

export const {
  setCurrentClinician,
  setSelectedClinician,
  setCliniciansList,
    setClinicStats,
  removeCurrentClinician,
  setLoading,
  setUpdateAvatarLoading,
  setSortConfig,
  setOffset,
  setLimit,
  setTotal,
  setSelectedClinicianRoles,
  setClinicTeamMembersList,
  setClinicTeamLeadsList,
  setClinicTeamMembersStringList,
  setClinicTeamLeadsStringList,
  setLimitPatients,
  setTotalPatients,
  setOffsetPatients,
  setSortConfigPatients,
  setClinicianPatientsList,
  setClinicianRecoveryTemplates,
  setSelectedRecoveryTemplate,
  setSearch,
  setClinicianPatientsLoading,
  removeRecoveryTemplate,
} = currentClinicianSlice.actions;
export const { reducer } = currentClinicianSlice;
